import { Component, useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import type { ClinicRole, StaffRole } from '../session/roles';
import { canAccess, sections as registeredSections, type SectionDefinition } from '../registry';

/** Ошибка фонового компонента раздела не должна ронять оболочку: компонент просто перестаёт работать. */
class ListenerBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

function SectionListener({ section }: { section: SectionDefinition }) {
  const [Listener, setListener] = useState<ComponentType | null>(null);
  useEffect(() => {
    let active = true;
    section
      .shellListener?.()
      .then((module) => {
        if (active) setListener(() => module.default);
      })
      .catch(() => {
        /* нет сети — фоновый компонент подключится при следующем входе */
      });
    return () => {
      active = false;
    };
  }, [section]);
  return Listener ? (
    <ListenerBoundary>
      <Listener />
    </ListenerBoundary>
  ) : null;
}

/** Фоновые компоненты разделов (SectionDefinition.shellListener), доступных ролям пользователя в этой области. */
export function SectionListeners({
  area,
  roles,
  sections = registeredSections,
}: {
  area: 'clinic' | 'backoffice';
  roles: ReadonlyArray<ClinicRole | StaffRole>;
  sections?: ReadonlyArray<SectionDefinition>;
}) {
  const list = useMemo(() => sections.filter((section) => section.area === area && section.shellListener && canAccess(section, roles)), [sections, area, roles]);
  return (
    <>
      {list.map((section) => (
        <SectionListener key={section.id} section={section} />
      ))}
    </>
  );
}
